import { ReactNode, type CSSProperties } from 'react';
import {
  Box,
  Flex,
  Button,
  Tab,
  TabList,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { IoExit } from 'react-icons/io5';

import Card from 'components/card/Card';
import FloatingSaveBar from './FloatingSaveBar';
import QuoteLockedBanner from './QuoteLockedBanner';
import { useQuoteLock, useAnswerCodes } from '../hooks';
import { LOB_PERSONAL_AUTO } from 'variables/constants';
import {
  getLobFromLineOfBusiness,
  type QuoteLOB,
  type QuoteStep,
  type QuoteNavigationState,
} from '../constants';

import type { OpenQuoteInfo } from 'hooks/useOpenQuotes';

// Strips the browser's default fieldset chrome so wrapping a locked step in
// one is invisible to layout. Deliberately no opacity here: fading the
// content itself makes the cards translucent and they lose their edges
// against the page background. The wash goes on top instead.
const LOCKED_FIELDSET_STYLE: CSSProperties = {
  border: 0,
  padding: 0,
  margin: 0,
  minWidth: 0,
};

// Steps that only present results. They stay functionally locked, but the
// wash adds nothing there and just makes the quote harder to read.
const NO_LOCK_WASH_STEPS = ['validations', 'quote'];

export const getQuoteTabLabel = (quoteInfo: OpenQuoteInfo): string => {
  const isAuto = quoteInfo.line_of_business === LOB_PERSONAL_AUTO;
  const type = isAuto ? 'Auto' : 'Home';
  return quoteInfo.rfq_ref ? `${type} #${quoteInfo.rfq_ref}` : type;
};

interface CarriersProceedProps {
  onProceed: () => void;
  isLoading: boolean;
  hasMvrError?: boolean;
}

interface AssignmentGetQuotesProps {
  onGetQuotes: () => void;
  isDisabled?: boolean;
}

interface QuoteLayoutProps {
  quoteId: number;
  lob: QuoteLOB;
  steps: QuoteStep[];
  navigationState: QuoteNavigationState;
  openQuotes: OpenQuoteInfo[];
  activeStepKey: string;
  onStepChange: (stepKey: string) => void;
  onQuoteChange: (quoteId: number, lob: QuoteLOB) => void;
  onExit: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasPendingChanges?: boolean;
  carriersProceed?: CarriersProceedProps;
  assignmentGetQuotes?: AssignmentGetQuotesProps;
  children: ReactNode;
}

const QuoteLayout = ({
  quoteId,
  lob,
  steps,
  navigationState: baseNavigationState,
  openQuotes,
  activeStepKey,
  onStepChange,
  onQuoteChange,
  onExit,
  onSave,
  isSaving,
  hasPendingChanges,
  carriersProceed,
  assignmentGetQuotes,
  children,
}: QuoteLayoutProps) => {
  const { isLocked } = useQuoteLock();
  const { isEffectiveDateValid } = useAnswerCodes();
  const navigationState: QuoteNavigationState = {
    ...baseNavigationState,
    effectiveDateUnsupported: !isEffectiveDateValid,
  };
  const showLockWash = isLocked && !NO_LOCK_WASH_STEPS.includes(activeStepKey);
  const lockedOverlayBg = useColorModeValue('whiteAlpha.600', 'blackAlpha.600');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const activeStepColor = useColorModeValue('brand.500', 'brand.400');
  const activeStepIndex = steps.findIndex((s) => s.key === activeStepKey);
  const activeQuoteIndex = openQuotes.findIndex((q) => q.id === quoteId);
  const showTabs = openQuotes.length > 1;

  const handlePrevious = () => {
    if (activeStepIndex > 0) {
      onStepChange(steps[activeStepIndex - 1].key);
    }
  };

  const handleNext = () => {
    if (activeStepIndex < steps.length - 1) {
      const nextStep = steps[activeStepIndex + 1];
      if (nextStep.isDisabled?.(navigationState)) return;
      onStepChange(nextStep.key);
    }
  };

  const isNextDisabled = (() => {
    if (activeStepIndex === steps.length - 1) return true;
    if (hasPendingChanges) return true;
    const nextStep = steps[activeStepIndex + 1];
    return nextStep?.isDisabled?.(navigationState) ?? false;
  })();

  const handleQuoteTabClick = (q: OpenQuoteInfo) => {
    if (hasPendingChanges) return;
    if (showTabs && q.id !== quoteId) {
      const targetLob = getLobFromLineOfBusiness(q.line_of_business);
      onQuoteChange(q.id, targetLob);
    }
  };

  const handleStepTabClick = (index: number) => {
    if (hasPendingChanges) return;
    const step = steps[index];
    if (step.isDisabled?.(navigationState)) return;
    onStepChange(step.key);
  };

  return (
    <>
      <Flex justify="space-between" align="start" pt={4}>
        <Tabs index={activeQuoteIndex} variant="enclosed">
          <TabList gap={1} border={0}>
            {openQuotes.map((q) => {
              const isOtherQuote = showTabs && q.id !== quoteId;
              const isDisabled = hasPendingChanges && isOtherQuote;
              return (
                <Tab
                  key={q.id}
                  onClick={() => handleQuoteTabClick(q)}
                  cursor={
                    isDisabled
                      ? 'not-allowed'
                      : isOtherQuote
                        ? 'pointer'
                        : 'default'
                  }
                  opacity={isDisabled ? 0.5 : 1}
                  _focus={{ boxShadow: 'none' }}
                  borderBottomColor="white"
                  bg="gray.200"
                  _selected={{
                    bg: 'white',
                    borderBottomColor: 'white',
                  }}
                >
                  <Text fontWeight="600" fontSize="sm">
                    {getQuoteTabLabel(q)}
                  </Text>
                </Tab>
              );
            })}
          </TabList>
        </Tabs>

        <Button
          leftIcon={<IoExit />}
          size="sm"
          variant="outline"
          onClick={onExit}
        >
          Exit
        </Button>
      </Flex>
      <Card p={0} overflow="hidden" roundedTop={0}>
        <Tabs mt="8" index={activeStepIndex} onChange={handleStepTabClick}>
          <TabList
            borderBottom={0}
            mx={4}
            px={4}
            bg="gray.200"
            rounded="lg"
            justifyItems="between"
            flex={1}
          >
            {steps.map((step, index) => {
              const isStepDisabled =
                step.isDisabled?.(navigationState) ?? false;
              const isPendingDisabled =
                hasPendingChanges && activeStepIndex !== index;
              const isDisabled = isStepDisabled || isPendingDisabled;
              const tooltipLabel = isStepDisabled
                ? step.disabledTooltip?.(navigationState)
                : undefined;

              const tab = (
                <Tab
                  key={step.key}
                  fontSize="sm"
                  fontWeight="600"
                  py={3}
                  px={4}
                  flex={1}
                  isDisabled={isDisabled}
                  width="100%"
                  color={
                    activeStepIndex === index ? activeStepColor : 'gray.500'
                  }
                  borderBottom="2px solid"
                  borderBottomColor={
                    activeStepIndex === index ? 'brand.500' : 'transparent'
                  }
                  _focus={{ boxShadow: 'none' }}
                  _selected={{
                    color: activeStepColor,
                    borderBottomColor: 'brand.500',
                  }}
                  _hover={{
                    color: isDisabled ? undefined : activeStepColor,
                  }}
                  _disabled={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                >
                  <Text whiteSpace="nowrap">{step.label}</Text>
                </Tab>
              );

              if (tooltipLabel) {
                return (
                  <Tooltip key={step.key} label={tooltipLabel} hasArrow>
                    <Box flex={1}>{tab}</Box>
                  </Tooltip>
                );
              }

              return tab;
            })}
          </TabList>
        </Tabs>

        <Box p={4} minH="400px">
          <QuoteLockedBanner />
          {isLocked && !NO_LOCK_WASH_STEPS.includes(activeStepKey) ? (
            <Box position="relative">
              <fieldset disabled style={LOCKED_FIELDSET_STYLE}>
                {children}
              </fieldset>
              {showLockWash && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  zIndex={5}
                  bg={lockedOverlayBg}
                  borderRadius="md"
                  pointerEvents="none"
                />
              )}
            </Box>
          ) : (
            children
          )}
        </Box>

        <Flex
          justify="flex-end"
          gap={3}
          p={4}
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <Button
            leftIcon={<ChevronLeftIcon />}
            variant="outline"
            onClick={handlePrevious}
            isDisabled={activeStepIndex === 0 || hasPendingChanges}
          >
            Previous
          </Button>
          {/* A locked quote has already been through the market-selection
              flow, so Carriers falls back to plain step navigation. */}
          {activeStepKey === 'carriers' && !isLocked ? (
            <Button
              rightIcon={<ChevronRightIcon />}
              variant="brand"
              onClick={carriersProceed?.onProceed}
              isDisabled={
                !navigationState.hasCarrierSelected ||
                hasPendingChanges ||
                navigationState.effectiveDateUnsupported
              }
              isLoading={carriersProceed?.isLoading}
              loadingText={
                carriersProceed?.hasMvrError ? 'Processing...' : 'Validating...'
              }
            >
              {carriersProceed?.hasMvrError
                ? 'Proceed Without MVR'
                : 'Proceed With Selected Market'}
            </Button>
          ) : activeStepKey === 'assignment' &&
            !isLocked &&
            assignmentGetQuotes ? (
            <Button
              rightIcon={<ChevronRightIcon />}
              variant="brand"
              onClick={assignmentGetQuotes.onGetQuotes}
              isDisabled={isNextDisabled || assignmentGetQuotes.isDisabled}
            >
              Get quotes
            </Button>
          ) : (
            <Button
              rightIcon={<ChevronRightIcon />}
              variant="brand"
              onClick={handleNext}
              isDisabled={isNextDisabled}
            >
              Next
            </Button>
          )}
        </Flex>
      </Card>

      {hasPendingChanges && onSave && (
        <FloatingSaveBar onSave={onSave} isSaving={isSaving || false} />
      )}
    </>
  );
};

export default QuoteLayout;
